import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reportsActions } from 'store/actions';
import { revenueReportsAPI } from 'services/reports.service';
import { publisherReportsRequestTypes } from 'constants';

/*
  if you need to make the range larger - min possible day width will get more narrow
  consider hiding more dates around the hovered one (Chart.jsx),
  and adjust code for the last days that may not fit
*/
const MAX_CHART_RANGE = 180;

export const useFacade = ({ timePeriod, metrics }) => {
  const dispatch = useDispatch();
  const isChartHidden = useSelector((state) => state.reports.isChartHiddenReports);
  const selectedMetrics = useSelector((state) => state.reports.selectedMetricsReports);

  // get time period
  const numOfDays = timePeriod ? getNumOfDays(timePeriod.from, timePeriod.to) : 0;
  const isValidTimeRange = numOfDays < MAX_CHART_RANGE && numOfDays > 1;

  // get daily data from server
  const {
    data,
    isFetching: isFetchingDataChart,
    isSuccess: isChartDataLoaded,
  } = revenueReportsAPI.useGetReportQuery(
    {
      to: timePeriod.to,
      from: timePeriod.from,
      type: publisherReportsRequestTypes.date,
    },
    {
      skip: !timePeriod || !Object.entries(timePeriod).length || !isValidTimeRange,
    },
  );

  const chartData = data?.revenueData?.rows;

  // disable chart button
  useEffect(() => {
    if (isFetchingDataChart) return;

    dispatch(
      reportsActions.updateProperty({
        propField: 'isChartDisabledReports',
        newValue: !chartData,
      }),
    );
  }, [isFetchingDataChart, chartData]);

  // get data per day map, sums and number of days
  const { dataPerDay, totalValues } = useMemo(
    () => formatReportsData(chartData, timePeriod, metrics),
    [chartData],
  );

  // get prev period data
  const prevTimePeriod = useMemo(
    () => getPreviousPeriod(timePeriod.from, numOfDays),
    [timePeriod.from, numOfDays],
  );

  const { data: prevData, isFetching: isFetchingPrevDataChart } =
    revenueReportsAPI.useGetReportQuery(
      {
        to: prevTimePeriod.to,
        from: prevTimePeriod.from,
        type: publisherReportsRequestTypes.date,
      },
      {
        skip: !isChartDataLoaded || !isValidTimeRange,
      },
    );

  const prevChartData = prevData?.revenueData?.rows;

  // get sums for prev period
  const { totalPrevValues } = useMemo(
    () => formatPrevDataReports(prevChartData, metrics),
    [prevChartData],
  );

  // calculate difference in %
  const change = useMemo(
    () => (isFetchingPrevDataChart ? {} : getDifference(totalValues, totalPrevValues)),
    [isFetchingPrevDataChart, totalValues, totalPrevValues],
  );

  let errorMsg = '';

  if (!dataPerDay || (!isChartDataLoaded && !isFetchingDataChart)) errorMsg = 'No data';
  if (numOfDays >= 180) errorMsg = 'The range is too large. Select smaller amount of days';
  if (numOfDays < 2) errorMsg = 'Please select a wider date range to display the graph';

  return {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isError: !isChartDataLoaded && !isValidTimeRange,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    selectedMetrics,
    isChartHidden,
  };
};

function formatReportsData(data, timePeriod, metrics) {
  if (!data || !timePeriod.from || !timePeriod.to)
    return { dataPerDay: new Map(), totalValues: {} };

  const [fromYear, fromMonth, fromDay] = timePeriod.from.split('/').map(Number);
  const [toYear, toMonth, toDay] = timePeriod.to.split('/').map(Number);

  let startDate = new Date(Date.UTC(fromYear, fromMonth - 1, fromDay));
  const endDate = new Date(Date.UTC(toYear, toMonth - 1, toDay));

  let totalValues = {};
  metrics.forEach((m) => (totalValues[m] = 0));

  let dataPerDay = new Map();
  while (startDate.getTime() <= endDate.getTime()) {
    const key = [
      startDate.getUTCFullYear(),
      String(startDate.getUTCMonth() + 1).padStart(2, '0'),
      String(startDate.getUTCDate()).padStart(2, '0'),
    ].join(',');

    dataPerDay.set(key, structuredClone(totalValues));
    startDate.setUTCDate(startDate.getUTCDate() + 1);
  }

  data.forEach((day) => {
    // add the day data for the map
    if (day.date) {
      let daySum = {};

      const cur = dataPerDay.get(day.date);
      metrics.forEach((m) => (daySum[m] = cur[m] + (day[m] || 0)));
      dataPerDay.set(day.date, daySum);

      // add this day values to total sums object
      metrics.forEach((m) => (totalValues[m] += day[m] || 0));
    }
  });

  // calculate total ctr and eCPM
  totalValues.ctr = totalValues.impressions
    ? (totalValues.clicks * 100) / totalValues.impressions
    : 0;
  totalValues.eCPM = totalValues.impressions
    ? (totalValues.earning * 1000) / totalValues.impressions
    : 0;

  return { dataPerDay, totalValues };
}

function formatPrevDataReports(data, metrics) {
  if (!data) return {};

  let totalValues = {};
  metrics.forEach((m) => (totalValues[m] = 0));

  data.forEach((day) => {
    if (day.date) {
      // add day values to total sums object
      metrics.forEach((m) => {
        totalValues[m] += day[m];
      });
    }
  });

  // recalculate total ctr and eCPM
  totalValues.ctr = totalValues.impressions
    ? (totalValues.clicks * 100) / totalValues.impressions
    : 0;
  totalValues.eCPM = totalValues.impressions
    ? (totalValues.earning * 1000) / totalValues.impressions
    : 0;

  return { totalPrevValues: totalValues };
}

function getDifference(objCur, objPrev) {
  if (!objCur || !objPrev) return {};

  const change = {};

  Object.keys(objCur).forEach((key) => {
    const cur = objCur[key] && !isNaN(objCur[key]) ? +objCur[key].toFixed(2) : 0;
    const prev = objPrev[key] && !isNaN(objPrev[key]) ? +objPrev[key].toFixed(2) : 0;

    if (cur === 0) {
      change[key] = prev ? -100 : 0;
    } else if (prev === 0) {
      change[key] = cur ? 100 : 0;
    } else {
      change[key] = ((objCur[key] - objPrev[key]) / objPrev[key]) * 100;
    }
  });

  return change;
}

function getNumOfDays(from, to) {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return Math.floor((toDate - fromDate) / (24 * 60 * 60 * 1000)) + 1;
}

function getPreviousPeriod(from, days) {
  const fromDate = new Date(from);

  const previousFromDate = new Date(fromDate.getTime() - days * 24 * 60 * 60 * 1000);
  const previousToDate = new Date(fromDate.getTime() - 1 * 24 * 60 * 60 * 1000);

  const formattedPreviousFrom = formatDate(previousFromDate);
  const formattedPreviousTo = formatDate(previousToDate);

  return {
    from: formattedPreviousFrom,
    to: formattedPreviousTo,
  };
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}
